import * as React from 'react';
import { Component } from 'react';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { translate } from './Translate';
import formatter from '../utils/formatter';
import LineChart from 'recharts/es6/chart/LineChart';
import Line from 'recharts/es6/cartesian/Line';
import XAxis from 'recharts/es6/cartesian/XAxis';
import YAxis from 'recharts/es6/cartesian/YAxis';
import CartesianGrid from 'recharts/es6/cartesian/CartesianGrid';
import ResponsiveContainer from 'recharts/es6/component/ResponsiveContainer';
import Tooltip from 'recharts/es6/component/Tooltip';
import Legend from 'recharts/es6/component/Legend';
import { computed, action } from 'mobx';
import { RebaseHelper } from '@kurtosys/ksys-app-template/dist/helpers';

const ResponsiveChartContainer = styled<any, any>(ResponsiveContainer)`
    ${propStyles};
`;

ResponsiveChartContainer.defaultProps = {
    fontSize: 'xs',
};

@inject('dataStore')
@inject('translations')
@observer
class ChartLineChart extends Component<any, any> {
    rebaser: RebaseHelper;

    constructor(props) {
        super(props);
        const { rebase } = props.config;
        this.rebaser = new RebaseHelper(rebase);
    }

    @computed
    get data() {
        const { rebase } = this.props.config;
        const { data } = this.props;

        if (rebase && rebase.enabled) {
            return this.rebaser.rebase(data);
        }

        return data;
    }

    @computed
    get yDomain() {
        const { yAxis, series } = this.props;
        const { getTicksAroundPoint, data } = this;
        let values = [];
        // Get values from each series data key for min and max
        series.forEach(element => {
            data.map(row => {
                const value = row[element.dataKey];
                if (value) {
                    values.push(value);
                }
            });
        });

        let min = Math.min(...values);
        let max = Math.max(...values);

        // readjust domain min and max based on ticks
        if (yAxis.basePoint) {
            values = getTicksAroundPoint(
                yAxis.basePoint,
                min,
                max,
                yAxis.tickCount
            );
            min = Math.min(...values);
            max = Math.max(...values);
        }

        return [Math.floor(min), Math.ceil(max)];
    }

    @action
    getTicksAroundPoint(
        point: number,
        min: number,
        max: number,
        tickCount?: number
    ) {
        const count = tickCount || 5;
        const intervals = [
            0.01,
            0.02,
            0.05,
            0.1,
            0.2,
            0.5,
            1,
            2,
            5,
            10,
            20,
            50,
            100,
            200,
            500,
            1000,
            2000,
            5000,
            10000,
            20000,
            50000,
            10000000,
        ];
        const actualMax = Math.max(max, point);
        const actualMin = Math.min(min, point);
        const gap = (actualMax - actualMin) * 100;
        const bestInterval = intervals.find(
            interval => interval >= gap / count
        );
        const ticks: Array<number> = [];
        ticks.push(point);
        if (bestInterval) {
            let maxRunner = point;
            while (maxRunner < actualMax) {
                maxRunner += bestInterval / 100;
                ticks.push(maxRunner);
            }
            let minRunner = point;
            while (minRunner > actualMin) {
                minRunner -= bestInterval / 100;
                ticks.push(minRunner);
            }
        }

        return ticks.sort((a, b) => a - b);
    }

    render() {
        const {
            theme,
            dataStore,
            translations,
            config,
            xAxis,
            yAxis,
            series,
            getThemeColor,
            getSeriesName,
            locale,
            fundCurrency,
        } = this.props;

        const { yDomain, getTicksAroundPoint } = this;

        if (yAxis.basePoint) {
            // yAxis props are spread on yAxis component. Add ticks only if config for basePoint is available
            yAxis.ticks = getTicksAroundPoint(
                yAxis.basePoint,
                yDomain[0],
                yDomain[1],
                yAxis.tickCount
            );
        }

        return (
            <ResponsiveChartContainer
                aspect={3}
                {...theme.ResponsiveChartContainer}
            >
                <LineChart data={this.data}>
                    {config.grid && (
                        <CartesianGrid
                            {...config.grid}
                            stroke={getThemeColor(config.grid.stroke)}
                            fill={getThemeColor(config.grid.fill)}
                        />
                    )}
                    {config.tooltip && (
                        <Tooltip
                            cursor={{
                                strokeWidth: 0,
                                fill: 'rgba(0,0,0,0.05)',
                            }}
                            {...config.tooltip}
                            labelFormatter={v =>
                                config.tooltip.labelFormat
                                    ? formatter({
                                          dataStore,
                                          locale,
                                          format: config.tooltip.labelFormat,
                                          type: config.tooltip.labelFormat.type,
                                          currency: fundCurrency,
                                          options:
                                              config.tooltip.labelFormat
                                                  .options,
                                          value: v,
                                      })
                                    : v
                            }
                            formatter={v =>
                                config.tooltip.valueFormat
                                    ? formatter({
                                          dataStore,
                                          locale,
                                          format: config.tooltip.valueFormat,
                                          type: config.tooltip.valueFormat.type,
                                          currency: fundCurrency,
                                          options:
                                              config.tooltip.valueFormat
                                                  .options,
                                          value: v,
                                      })
                                    : v
                            }
                        />
                    )}
                    {config.legend && (
                        <Legend iconType="circle" {...config.legend} />
                    )}
                    {xAxis && (
                        <XAxis
                            label={xAxis.label}
                            dataKey={xAxis.dataKey}
                            tickLine={false}
                            {...xAxis}
                            tickFormatter={v =>
                                xAxis.format
                                    ? formatter({
                                          dataStore,
                                          locale,
                                          format: xAxis.format,
                                          type: xAxis.format.type,
                                          currency: fundCurrency,
                                          options: xAxis.format.options,
                                          value: v,
                                      })
                                    : v
                            }
                        />
                    )}
                    {yAxis && (
                        <YAxis
                            label={yAxis.label}
                            dataKey={yAxis.dataKey}
                            yAxisId="left"
                            orientation="left"
                            axisLine={false}
                            tickLine={false}
                            domain={yDomain}
                            {...yAxis}
                            tickFormatter={v =>
                                yAxis.format
                                    ? formatter({
                                          dataStore,
                                          locale,
                                          format: yAxis.format,
                                          type: yAxis.format.type,
                                          currency: fundCurrency,
                                          options: yAxis.format.options,
                                          value: v,
                                      })
                                    : v
                            }
                        />
                    )}
                    {series.map((s, index) => (
                        <Line
                            key={`line-${s.dataKey}-${index}`}
                            dataKey={s.dataKey}
                            yAxisId="left"
                            dot={false}
                            type="linear"
                            {...s}
                            name={
                                s.name
                                    ? getSeriesName(s)
                                    : translate(translations, s.label)
                            }
                            stroke={getThemeColor(s.color)}
                            isAnimationActive={!dataStore.isExpandAll}
                        />
                    ))}
                </LineChart>
            </ResponsiveChartContainer>
        );
    }
}

export default withTheme(ChartLineChart) as any;



// WEBPACK FOOTER //
// ./src/Components/ChartLineChart.tsx